/**
 * Emotion styles for the account dashboard: the row and block layout used by
 * `../CustomerDashboard.tsx`, the card styles of the address, delivery and payment cards, the
 * rep photo of `./YourMetalsExpert.tsx`, and the `chart*` / `SavedItem*` styles of the chart
 * widgets (`./DataVizWidget.tsx`, `./WhereYouSaved.tsx`).
 *
 * `Divider` (a faint 1px rule) is imported well beyond the dashboard: checkout previews, forms
 * and review cards, invoice payment methods, and the address, define-cut, bulk-upload and quote
 * modals. A change to `Divider` shows in all of its importers.
 * `components/OrderDetails/Helpers/StyledComponents.tsx` has its own copies of `outerRowStyles`,
 * `wideBlock` and `blocks`; only `../CustomerDashboard.tsx` uses this file's.
 */
import styled from "@emotion/styled";
import rgba from "polished/lib/color/rgba";
import { css } from "@emotion/react";

/** Lets a row of blocks wrap below `mediumBreakPoint` (px) and below `theme.breakpoints.lg`. */
export const outerRowStyles = (mediumBreakPoint: number, theme: any) => css`
	margin-bottom: 10px;
	@media screen and (max-width: ${mediumBreakPoint}px) {
		flex-flow: row wrap;
	}
	@media screen and (max-width: calc( ${theme.breakpoints.lg} - 1px)) {
		flex-flow: row wrap;
	}
`;

/**
 * A dashboard block that shares its row. `props`: `color` (background), `isMobile`, `theme`
 * and `mediumBreakPoint` (px). Between `lg` and `mediumBreakPoint` it is 49.4% wide (two to a
 * row); below `lg`, full width.
 */
export const wideBlock = (props: any) => css`
	background-color: ${props.color};
	width: 100%;
	margin-bottom: ${props.isMobile ? "10px" : "0"};
	padding: 18px;

	// IPad Size (50 /50 view of the two charts
	${props?.theme &&
	`	 
		@media screen and (min-width: ${props.mediumBreakPoint}px) { 
		 
			:not(last-child) {
				 margin-right: 10px;
			}
		}  
		@media screen and (max-width: ${props.mediumBreakPoint}px) { 
			margin-bottom:10px;
			width:49.4%; 
			:not(last-child) {
				 margin-right: 0px;
			} 
	   } 
		@media screen and (max-width: ${props.theme.breakpoints.lg}) { 
			 width: 100%;  
			 margin-right:0px;
			 
		}`}
`;

/** A full-width dashboard block; `props`: `color` (background) and `isMobile`. */
export const blocks = (props: any) => css`
	background-color: ${props.color};
	width: 100%;
	margin-bottom: ${props.isMobile ? "10px" : "0"};
	padding: 18px;
`;

/** The open-orders panel of `../CustomerDashboard.tsx`; `props`: `color`, `isMobile`. */
export const openOrdersSection = (props: any) => css`
	background-color: ${props.color};
	width: 100%;
	margin: 10px 0%;
	padding: ${props.isMobile ? "18px" : "0"};
`;

/** Legend label of `./WhereYouSaved.tsx` with a bullet in the slice's `color`. */
export const SavedItemName = styled.div`
	padding: 0;
	margin: 0;
	:before {
		content: "• ";
		font-size: 28px;
		position: relative;
		top: 6px;
	}
	${(props: any) => {
		return css`
			${props.fontColor && `color:${props.fontColor};`}
			${props.color && `:before {color:${props.color};}`} 
			@media only screen and (max-width: ${props.breakPoint}px) {
				//font-size: ${props.theme.typography.fonts.sizes.xs.size};
				:before {
					font-size: 16px;
					top: 2px;
				}
			}
		`;
	}}
`;

/** Legend share of `./WhereYouSaved.tsx`; appends "%". */
export const SavedItemVal = styled.div`
	padding: 0;
	margin: 0;
	height: auto;
	min-width: 20px;
	align-self: flex-end;
	:after {
		content: "%";
	}
	padding-left: 4px;
	${({ color }) => css`
		${color && `color:${rgba(color, 0.5)}`}
	`}
`;

/** Chart-widget title; hidden below `lg`, where the accordion header shows it instead. */
export const charTitleStyles = (theme: any) => css`
	width: 100%;
	padding: 0px 0px 18px 0px;
	border: 0px solid red;
	@media only screen and (max-width: calc( ${theme.breakpoints.lg} - 1px)) {
		display: none;
	}
`;

/** Chart-widget middle band (chart, legend, body text) between two `borderColor` rules. */
export const centerContainerStyles = (borderColor: string) => {
	return css`
		width: 100%;
		padding: 0px;
		border-top: 1px solid ${borderColor};
		border-bottom: 1px solid ${borderColor};
		margin-bottom: 14px;

		min-height: 190px;
	`;
};

/** Chart-widget row holding the chart and its legend, nudged 10px left. */
export const chartSection = css`
	position: relative;
	left: -10px;
	width: 100%;
	padding: 0;
	min-height: 112px;
	border: 0px solid yellow;
`;
/** Chart-widget legend column; `isMobile` drops its 8px left padding. */
export const chartInfoStyles = (isMobile: boolean) => css`
	width: 100%;
	padding-left: ${isMobile ? `0px` : `8px`};
	border: 0px solid yellow;
`;

/** Chart-widget body text; the `sm` font size at `lg` width and below. */
export const chartBodyText = (theme: any) => css`
	width: 100%;
	padding: 0px 0px 12px 0px;
	border: 0px solid red;
	min-height: 40px;
	@media only screen and (max-width: ${theme.breakpoints.lg}) {
		font-size: ${theme.typography.fonts.sizes.sm.size};
	}
`;

/** Chart-widget footer; fills the remaining height so the button sits at the bottom. */
export const chartFooterStyles = css`
	border: 0px solid red;
	flex: 1;
`;

/** Faint horizontal rule (`theme.colors.primary.darkBlue` at 10% opacity); needs `theme`. */
export const Divider = styled.div`
	width: 100%;
	opacity: 0.1;
	${(props: any) => {
		const { theme } = props;
		return css`
			border-bottom: 1px solid ${theme.colors.primary.darkBlue};
		`;
	}}
`;

/** The payment-card tile of `./DefaultPaymentMethod.tsx`; `props.background` is its color. */
export const cardStyle = (props: any) => css`
	margin: 20px 0 60px 0;
	background: ${props.background};
	border-radius: 5px;
	height: 160px;
	padding: 10px;
`;

/** Round photo frame of `./YourMetalsExpert.tsx` (100px, 75px below `lg`); needs `theme`. */
export const ContactDetailsImgWrapper = styled.span`
	display: inline-block;
	margin-right: 20px;
	vertical-align: middle;
	width: 100px;
	overflow: hidden;
	height: 100px;
	border-radius: 100px;
	${(props: any) => {
		const { theme } = props;
		return css`
			@media only screen and (max-width: ${theme.breakpoints.lg}) {
				margin-right: 15px;

				width: 75px;
				height: 75px;
				border-radius: 75px;
			}
		`;
	}}
`;

/** Fills `ContactDetailsImgWrapper` with the rep photo, cropped to cover. */
export const imageStyle = css`
	object-fit: cover;
	object-position: center center;
	height: 100%;
	width: 100%;
`;
